import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useTheme } from "@/components/ui/theme-provider";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const initialProfile = {
  fullName: "",
  company: "",
  email: "",
  telephone: "",
  contactConsent: false,
  privacyConsent: false,
  logoConsent: false,
};

// Custom ProfileForm component without the Next button
const WaitlistProfileForm = ({ 
  profile, 
  onProfileChange 
}: { 
  profile: typeof initialProfile;
  onProfileChange: (field: string, value: string | boolean) => void;
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Function to validate telephone number
  const validateTelephone = (telephone: string): boolean => {
    const phoneRegex = /^[\d\s\-+()]{10,20}$/;
    const digitCount = telephone.replace(/\D/g, '').length;
    return phoneRegex.test(telephone) && digitCount >= 10;
  };
  
  const handleFieldChange = (field: string, value: string | boolean) => {
    onProfileChange(field, value);
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }
  };
  
  return (
    <div className="space-y-6 text-foreground">
      <div className="space-y-2">
        <Label htmlFor="fullName" className="text-foreground flex items-center">
          Full Name <span className="text-[#00DC82] ml-1">*</span>
        </Label>
        <Input
          id="fullName"
          type="text"
          required
          value={profile.fullName}
          onChange={(e) => handleFieldChange("fullName", e.target.value)}
          className={`${isDarkMode ? 'bg-[#171f33]' : 'bg-white'} border-green-500/30 text-foreground focus:border-green-500 focus:ring-green-500 text-base md:text-sm h-12 md:h-10 ${formErrors.fullName ? 'border-[#00DC82]' : ''}`}
          inputMode="text"
          autoCapitalize="words"
          placeholder="Enter your name..."
        />
        {formErrors.fullName && (
          <p className="text-[#00DC82] text-sm mt-1">{formErrors.fullName}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="company" className="text-foreground flex items-center">
          Company <span className="text-[#00DC82] ml-1">*</span>
        </Label>
        <Input
          id="company"
          type="text"
          required
          value={profile.company}
          onChange={(e) => handleFieldChange("company", e.target.value)}
          className={`${isDarkMode ? 'bg-[#171f33]' : 'bg-white'} border-green-500/30 text-foreground focus:border-green-500 focus:ring-green-500 text-base md:text-sm h-12 md:h-10 ${formErrors.company ? 'border-[#00DC82]' : ''}`}
          inputMode="text"
          autoCapitalize="words"
          placeholder="Enter your company name..."
        />
        {formErrors.company && (
          <p className="text-[#00DC82] text-sm mt-1">{formErrors.company}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email" className="text-foreground flex items-center">
          Email Address <span className="text-[#00DC82] ml-1">*</span>
        </Label>
        <Input
          id="email"
          type="email"
          required
          value={profile.email}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          className={`${isDarkMode ? 'bg-[#171f33]' : 'bg-white'} border-green-500/30 text-foreground focus:border-green-500 focus:ring-green-500 text-base md:text-sm h-12 md:h-10 ${formErrors.email ? 'border-[#00DC82]' : ''}`}
          inputMode="email"
          autoCapitalize="none"
          autoComplete="email"
          placeholder="Enter your email address..."
        />
        {formErrors.email && (
          <p className="text-[#00DC82] text-sm mt-1">{formErrors.email}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="telephone" className="text-foreground flex items-center">
          Telephone Number <span className="text-[#00DC82] ml-1">*</span>
        </Label>
        <Input
          id="telephone"
          type="tel"
          required
          value={profile.telephone}
          onChange={(e) => handleFieldChange("telephone", e.target.value)}
          className={`${isDarkMode ? 'bg-[#171f33]' : 'bg-white'} border-green-500/30 text-foreground focus:border-green-500 focus:ring-green-500 text-base md:text-sm h-12 md:h-10 ${formErrors.telephone ? 'border-[#00DC82]' : ''}`}
          inputMode="tel"
          autoComplete="tel"
          placeholder="Enter your telephone number..."
        />
        {formErrors.telephone ? (
          <p className="text-[#00DC82] text-sm mt-1">{formErrors.telephone}</p>
        ) : (
          <p className="text-gray-500 text-xs mt-1">Enter a valid phone number with country code</p>
        )}
      </div>

      {/* Company logo consent checkbox */}
      <div className="flex items-start gap-2">
        <Checkbox 
          id="logoConsent" 
          checked={profile.logoConsent}
          onCheckedChange={(checked) => handleFieldChange("logoConsent", checked === true)}
          className="border-green-500/70 data-[state=checked]:bg-green-500 data-[state=checked]:text-white h-5 w-5 mt-0.5"
        />
        <Label 
          htmlFor="logoConsent" 
          className="text-sm text-foreground/80 cursor-pointer leading-tight"
        >
          I consent to having my company logo displayed as an early adopter partner
        </Label>
      </div>
      
      {/* Marketing consent checkbox */}
      <div className="flex items-start gap-2">
        <Checkbox 
          id="contactConsent" 
          checked={profile.contactConsent}
          onCheckedChange={(checked) => handleFieldChange("contactConsent", checked === true)}
          className="border-green-500/70 data-[state=checked]:bg-green-500 data-[state=checked]:text-white h-5 w-5 mt-0.5"
        />
        <Label 
          htmlFor="contactConsent" 
          className="text-sm text-foreground/80 cursor-pointer leading-tight"
        >
          I agree to be contacted by Sentiri about their products and services
        </Label>
      </div>
      
      {/* Privacy policy checkbox */}
      <div className="flex items-start gap-2">
        <Checkbox 
          id="privacyConsent" 
          checked={profile.privacyConsent}
          onCheckedChange={(checked) => {
            handleFieldChange("privacyConsent", checked === true);
            if (formErrors.privacyConsent && checked) {
              setFormErrors(prev => ({ ...prev, privacyConsent: "" }));
            }
          }}
          className={`border-green-500/70 data-[state=checked]:bg-green-500 data-[state=checked]:text-white h-5 w-5 mt-0.5 ${formErrors.privacyConsent ? 'border-[#00DC82]' : ''}`}
        />
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <Label 
              htmlFor="privacyConsent" 
              className="text-sm text-foreground/80 cursor-pointer"
            >
              I agree to Sentiri's
            </Label>
            <Link to="/privacy" className="text-green-500 hover:underline text-sm" target="_blank">
              Privacy Policy
            </Link>
            <span className="text-[#00DC82]">*</span>
          </div>
          {formErrors.privacyConsent && (
            <p className="text-[#00DC82] text-sm">{formErrors.privacyConsent}</p>
          )}
        </div>
      </div>
      
      <div className="pt-2">
        <p className="text-sm text-gray-500 mb-3">
          <span className="text-[#00DC82]">*</span> Required fields
        </p>
      </div>
    </div>
  );
};

export default function Waitlist() {
  const [profile, setProfile] = useState(initialProfile);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => {
    // Check localStorage on initial render
    const hasSeenWaitlistWelcome = localStorage.getItem('hasSeenWaitlistWelcome');
    return !hasSeenWaitlistWelcome;
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  // Update localStorage when welcome state changes
  useEffect(() => {
    if (!showWelcome) {
      localStorage.setItem('hasSeenWaitlistWelcome', 'true');
    }
  }, [showWelcome]);

  const handleProfileChange = (field: string, value: string | boolean) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!profile.fullName.trim()) {
      errors.fullName = "Full name is required";
    }
    
    if (!profile.company.trim()) {
      errors.company = "Company is required";
    }
    
    if (!profile.email.trim()) {
      errors.email = "Email is required";
    } else if (!profile.email.includes('@') || !profile.email.includes('.')) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!profile.telephone.trim()) {
      errors.telephone = "Telephone number is required";
    } else if (!/^[\d\s\-+()]{10,20}$/.test(profile.telephone) || profile.telephone.replace(/\D/g, '').length < 10) {
      errors.telephone = "Please enter a valid telephone number (at least 10 digits)";
    }
    
    if (!profile.privacyConsent) {
      errors.privacyConsent = "You must agree to the Privacy Policy";
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Check for existing email
      const { data: existingUser, error: checkError } = await supabase
        .from("waitlist")
        .select("id")
        .eq("email", profile.email)
        .maybeSingle();
      if (checkError) throw checkError;
      
      if (existingUser) {
        // Update existing waitlist entry
        const { error: updateError } = await supabase
          .from("waitlist")
          .update({
            full_name: profile.fullName,
            company: profile.company,
            telephone: profile.telephone,
            contact_consent: profile.contactConsent,
            privacy_policy_accepted: profile.privacyConsent,
            logo_consent: profile.logoConsent,
          })
          .eq("id", existingUser.id);
        
        if (updateError) throw updateError;
        
        setSuccess(true);
        toast({
          title: "Profile Updated!",
          description: "Your waitlist profile has been updated successfully.",
          variant: "default",
        });
      } else {
        // Insert new waitlist entry
        const { error: insertError } = await supabase.from("waitlist").insert({
          full_name: profile.fullName,
          company: profile.company,
          email: profile.email,
          telephone: profile.telephone,
          contact_consent: profile.contactConsent,
          privacy_policy_accepted: profile.privacyConsent,
          logo_consent: profile.logoConsent,
        });
        if (insertError) throw insertError;
        
        // Increment waitlist count for new users
        const { error: incrementError } = await supabase.rpc('increment_waitlist_count');
        if (incrementError) {
          console.error('Error incrementing count:', incrementError);
          // Don't throw error here as the main operation succeeded
        }
        
        setSuccess(true);
        toast({
          title: "Success!",
          description: "You have joined the waitlist.",
          variant: "default",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to join waitlist.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show welcome dialog first
  if (showWelcome) {
    return (
      <Dialog open={showWelcome} onOpenChange={setShowWelcome}>
        <DialogContent className="sm:max-w-md border-[#00DC82]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center mb-4">
              Join Our Exclusive Waitlist
            </DialogTitle>
            <DialogDescription className="text-center space-y-4">
              <p className="text-lg">
                Thank you for your interest in Sentiri! Join our exclusive waitlist to be among the first to access our revolutionary construction manufacturing platform.
              </p>
              <p>
                Limited to 50 companies - secure your spot today and get early access to exclusive features and special pricing.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-6">
            <Button 
              onClick={() => setShowWelcome(false)}
              className="bg-[#00DC82] text-white hover:bg-[#00DC82]/90"
            >
              Join Waitlist
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex flex-col p-4 pt-16">
        <div className="w-full max-w-lg mx-auto">
          <div className="relative bg-card rounded-lg shadow-lg border border-[#00DC82] bg-background">
            <div className="absolute inset-0 opacity-[0.03] bg-gradient-to-br from-[#00DC82] to-[#00A3FF]" />
            <div className="relative p-6 text-center">
              <h2 className="text-2xl font-bold mb-4">Welcome to the Waitlist!</h2>
              <p className="mb-6">You've successfully joined our exclusive waitlist. We'll be in touch soon with updates about early access and exclusive opportunities.</p>
              <Button 
                onClick={() => navigate("/")}
                className="bg-[#00DC82] text-white hover:bg-[#00DC82]/90"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col p-4 pt-16">
      <div className="w-full max-w-lg mx-auto">
        <div className="relative bg-card rounded-lg shadow-lg border border-[#00DC82] bg-background">
          <div className="absolute inset-0 opacity-[0.03] bg-gradient-to-br from-[#00DC82] to-[#00A3FF]" />
          <div className="relative p-6">
            <button
              onClick={() => navigate("/")}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
            
            <h3 className="text-2xl font-bold mb-6">Join the Waitlist</h3>
            
            <WaitlistProfileForm
              profile={profile}
              onProfileChange={handleProfileChange}
            />
            
            <Button
              className="mt-6 w-full bg-[#00DC82] text-white hover:bg-[#00DC82]/90"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Join Waitlist"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 